import { Fragment, useState } from 'react';
import { Dialog, Transition, Disclosure } from '@headlessui/react';
import apiClient from '../../api/client';
import { APP_CONFIG } from '../../config/constants';

// Tipos - Exportados para uso externo
export interface PluginVersion {
    id: number;
    version: string;
    changelog: string;
    createdAt: string;
    fileUrl: string;
}

export interface PluginWithVersions {
    id: string;
    name: string;
    versions?: PluginVersion[];
}

export interface DownloadModalProps {
    isOpen: boolean;
    closeModal: () => void;
    plugin: PluginWithVersions | null;
}

const ChevronUpIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    </svg>
);

export function DownloadModal({ isOpen, closeModal, plugin }: DownloadModalProps) {
    const [error, setError] = useState('');
    const [loadingVersion, setLoadingVersion] = useState<number | null>(null);
    const [downloadStarted, setDownloadStarted] = useState<number | null>(null);

    const handleDownload = async (versionId: number, versionName: string) => {
        if (downloadStarted === versionId) {
            return;
        }

        setError('');
        setLoadingVersion(versionId);
        try {
            const response = await apiClient.get(`/download/${versionId}`);

            if (response.data.downloadUrl) {
                const downloadUrl = response.data.downloadUrl;
                const fullUrl = downloadUrl.startsWith('http')
                    ? downloadUrl
                    : `${APP_CONFIG.uploadsBaseUrl}${downloadUrl}`;

                // Obter extensão do arquivo original
                const originalFileName = downloadUrl.split('/').pop() || '';
                const extension = originalFileName.includes('.')
                    ? originalFileName.substring(originalFileName.lastIndexOf('.'))
                    : '.jar';

                // Criar nome amigável: NomeDoPlugin-v1.0.0.jar
                const pluginNameClean = plugin?.name.replace(/[^a-zA-Z0-9-_]/g, '-') || 'plugin';
                const fileName = `${pluginNameClean}-v${versionName}${extension}`;

                // Fazer download do arquivo
                try {
                    const fileResponse = await fetch(fullUrl, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });

                    if (!fileResponse.ok) {
                        throw new Error('Erro ao baixar o arquivo');
                    }

                    const blob = await fileResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    setDownloadStarted(versionId);
                    setTimeout(() => setDownloadStarted(null), 5000);
                } catch (fetchError) {
                    console.error('Erro ao fazer download:', fetchError);
                    // Fallback: abrir em nova aba se o download direto falhar
                    const link = document.createElement('a');
                    link.href = fullUrl;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    setDownloadStarted(versionId);
                    setTimeout(() => setDownloadStarted(null), 3000);
                }
            } else {
                setError("Link de download indisponível no momento.");
            }
        } catch (err: any) {
            let errorMessage = 'Falha ao obter o link de download.';
            if (err.response?.status === 403) {
                errorMessage = 'Você não possui acesso a este plugin.';
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            }
            setError(errorMessage);
        } finally {
            setLoadingVersion(null);
        }
    };

    if (!plugin) return null;

    const sortedVersions = plugin.versions?.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ) || [];

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeModal}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all">
                                {/* Header com gradiente */}
                                <div className="bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 px-6 py-5 relative overflow-hidden">
                                    <div className="absolute inset-0 opacity-10">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
                                    </div>
                                    <div className="relative flex items-center gap-3">
                                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="text-xl font-bold text-white">
                                                Download de Versões
                                            </Dialog.Title>
                                            <p className="text-blue-100 text-sm">{plugin.name}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Conteúdo */}
                                <div className="p-6">
                                    {error && (
                                        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                                            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <div>
                                                <p className="text-red-900 font-semibold text-sm">Erro ao baixar</p>
                                                <p className="text-red-700 text-sm mt-1">{error}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                                        <div className="flex items-start gap-3">
                                            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <div className="flex-1">
                                                <p className="text-sm text-blue-900 font-medium">
                                                    {sortedVersions.length} {sortedVersions.length === 1 ? 'versão disponível' : 'versões disponíveis'}
                                                </p>
                                                <p className="text-xs text-blue-700 mt-1">
                                                    Selecione a versão que deseja baixar. Recomendamos sempre usar a versão mais recente.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
                                        {sortedVersions.length > 0 ? (
                                            sortedVersions.map((version, index) => (
                                                <Disclosure key={version.id} as="div" className="bg-gradient-to-br from-white to-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-blue-300 transition-all shadow-sm hover:shadow-md">
                                                    {({ open }) => (
                                                        <>
                                                            <div className="flex w-full items-center justify-between p-4">
                                                                <div className="flex items-center gap-3 flex-1">
                                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${index === 0 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                                        <svg className={`w-5 h-5 ${index === 0 ? 'text-brand-blue' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                                        </svg>
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <p className="font-bold text-gray-900">v{version.version}</p>
                                                                            {index === 0 && (
                                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                    Mais recente
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                            </svg>
                                                                            {new Date(version.createdAt).toLocaleDateString('pt-BR', {
                                                                                day: '2-digit',
                                                                                month: 'long',
                                                                                year: 'numeric',
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            })}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {version.changelog && (
                                                                        <Disclosure.Button className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium text-gray-700">
                                                                            <span>Changelog</span>
                                                                            <ChevronUpIcon className={`w-4 h-4 inline-block ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
                                                                        </Disclosure.Button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleDownload(version.id, version.version)}
                                                                        disabled={loadingVersion === version.id || downloadStarted === version.id}
                                                                        className="bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 text-white font-semibold px-5 py-2 rounded-lg text-sm hover:from-blue-600 hover:via-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none flex items-center gap-2"
                                                                    >
                                                                        {loadingVersion === version.id ? (
                                                                            <>
                                                                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                                Preparando...
                                                                            </>
                                                                        ) : downloadStarted === version.id ? (
                                                                            <>
                                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                                </svg>
                                                                                Baixado!
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                                </svg>
                                                                                Baixar
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {version.changelog && (
                                                                <Transition
                                                                    as={Fragment}
                                                                    enter="transition duration-200 ease-out"
                                                                    enterFrom="transform scale-95 opacity-0"
                                                                    enterTo="transform scale-100 opacity-100"
                                                                    leave="transition duration-150 ease-in"
                                                                    leaveFrom="transform scale-100 opacity-100"
                                                                    leaveTo="transform scale-95 opacity-0"
                                                                >
                                                                    <Disclosure.Panel className="px-4 pb-4 pt-2 border-t border-gray-200 bg-white">
                                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                                                                <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                                </svg>
                                                                                Notas de Atualização
                                                                            </h4>
                                                                            <div className="text-sm text-gray-700 space-y-1">
                                                                                {version.changelog.split('\n').map((line, i) => (
                                                                                    <p key={i} className={line.trim().startsWith('-') || line.trim().startsWith('•') ? 'ml-2' : ''}>{line}</p>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </Disclosure.Panel>
                                                                </Transition>
                                                            )}
                                                        </>
                                                    )}
                                                </Disclosure>
                                            ))
                                        ) : (
                                            <div className="text-center py-12">
                                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                </div>
                                                <p className="text-gray-500 font-medium">Nenhuma versão disponível</p>
                                                <p className="text-gray-400 text-sm mt-1">Entre em contato com o suporte</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

